import {
  MAX_SCHEMATIC_NODE_POINTS,
  type NetworkSchematic,
  type SchematicNetwork,
  type SchematicNode,
  type SchematicNodeKind,
  type SchematicPoint,
  type SchematicStroke,
  type SchematicStrokeWidth,
} from '@ghp/domain'

import type { SchematicViewport } from './schematic-viewport'

/**
 * Les gestes de l'éditeur de schéma, **en fonctions pures sur le document** (`@ghp/domain`).
 *
 * ⚠️ Rien ici ne connaît React, le DOM ni SVG, et c'est la frontière qui compte : le document
 * est le contrat, l'éditeur n'en est qu'une implémentation. Le jour où une bibliothèque de
 * canvas s'imposerait, ce fichier reste — c'est le rendu qui change.
 *
 * Toutes ces fonctions **rendent un document neuf** : un état React se remplace, il ne se mute
 * pas. Muter en place ferait rendre à l'annulation un dessin déjà modifié, c'est-à-dire un
 * bouton « Annuler » sans effet — et sur un schéma de terrain, un geste perdu ne se retrouve
 * pas.
 */

/**
 * La couleur **intrinsèque** des éléments réseau : c'est à elle qu'un valideur reconnaît une
 * conduite d'eaux pluviales d'une conduite d'eaux usées sur le dessin d'un agent.
 *
 * ⚠️ Elle prime sur la couleur choisie dans la colonne de droite, et ce n'est pas une
 * commodité : laisser peindre une EU en bleu, la couleur de l'EP, rendrait le schéma illisible
 * pour le bureau, qui n'a que ce dessin et la photo pour se représenter le branchement. Les
 * formes libres — rectangle, flèche, texte — n'ont pas cette contrainte : elles annotent, elles
 * ne nomment rien.
 */
const ELEMENT_STROKES: Partial<Record<SchematicNodeKind, SchematicStroke>> = {
  manhole: 'ink',
  inspectionChamber: 'ink',
  trap: 'stormwater',
  septicTank: 'ink',
  wastewaterPipe: 'wastewater',
  stormwaterPipe: 'stormwater',
  publicNetwork: 'wastewater',
  manholeCover: 'wastewater',
  gratedInlet: 'wastewater',
  floorDrain: 'wastewater',
  liftingStation: 'wastewater',
  downpipe: 'stormwater',
  plug: 'wastewater',
  trapHorizontal: 'wastewater',
  trapVertical: 'wastewater',
  gratedChannel: 'stormwater',
  downpipeOutfall: 'stormwater',
}

/**
 * Les éléments qui se disent **EU, EP ou unitaire** : leur réseau prime alors sur la couleur
 * par défaut ci-dessus. Ce sont ceux que GHP DIGIT déclinait par réseau — tabouret, tampon,
 * regard à grille, bonde de sol, poste de relevage, réseau public — et le regard, qui y existait
 * aussi neutre : sa couleur par défaut reste celle du regard neutre. Le siphon, le bouchon, la
 * gouttière — sur sol comprise — et la fosse ne se typent pas : leur réseau est fixé par ce
 * qu'ils sont.
 */
const NETWORK_TYPED_KINDS: ReadonlySet<string> = new Set([
  'manhole',
  'inspectionChamber',
  'publicNetwork',
  'manholeCover',
  'gratedInlet',
  'floorDrain',
  'liftingStation',
])

/**
 * Les éléments qui se disent **EU ou EP, jamais unitaire ni neutre** : le caniveau à grille
 * (« Couleur Caniveau EU et EP », #579). Sans réseau, il reste EP, la couleur de ceux déjà
 * dessinés.
 */
const SEPARATE_NETWORK_KINDS: ReadonlySet<string> = new Set(['gratedChannel'])

/** L'élément se dit-il EU, EP ou unitaire ? C'est ce qui ouvre le choix du réseau à l'écran. */
export function isNetworkTyped(kind: string): boolean {
  return NETWORK_TYPED_KINDS.has(kind) || SEPARATE_NETWORK_KINDS.has(kind)
}

/** L'élément ne se dit-il qu'EU ou EP ? Son choix de réseau n'offre alors que ces deux-là. */
export function isSeparateNetworkKind(kind: string): boolean {
  return SEPARATE_NETWORK_KINDS.has(kind)
}

/** Le réseau que l'élément accepte de porter, ou rien s'il ne se type pas dans celui-là. */
function acceptedNetwork(
  kind: string,
  network: SchematicNetwork | 'ink' | undefined,
): SchematicNetwork | 'ink' | undefined {
  if (NETWORK_TYPED_KINDS.has(kind)) return network
  if (SEPARATE_NETWORK_KINDS.has(kind) && (network === 'wastewater' || network === 'stormwater')) {
    return network
  }
  return undefined
}

export interface NewSchematicNode {
  /** Une uuid v7 tirée par l'appelant (M7) — jamais ici : une fonction pure ne tire rien. */
  id: string
  kind: SchematicNodeKind
  points: SchematicPoint[]
  /** La couleur choisie dans la colonne de droite ; ignorée sur un élément réseau. */
  stroke: SchematicStroke
  strokeWidth: SchematicStrokeWidth
  /** Le réseau d'un élément qui se type ; ignoré sur les autres. */
  network?: SchematicNetwork
  text?: string | null
  /** L'étage d'une étiquette de pièce. */
  level?: number | null
}

/**
 * Compose une forme du dessin, **couleur intrinsèque appliquée**.
 *
 * Passer par cette fabrique plutôt que d'écrire l'objet à la main est ce qui garantit qu'un
 * seul endroit décide de la couleur d'un élément réseau : trois appelants qui composent le
 * nœud eux-mêmes finissent par diverger, et la divergence ne se voit qu'à la relecture du
 * dessin, six mois plus tard.
 */
export function createSchematicNode({
  id,
  kind,
  points,
  stroke,
  strokeWidth,
  network,
  text,
  level,
}: NewSchematicNode): SchematicNode {
  const typed = acceptedNetwork(kind, network)

  return {
    id,
    kind,
    points,
    stroke: typed ?? ELEMENT_STROKES[kind] ?? stroke,
    strokeWidth,
    text: text ?? null,
    ...(level === undefined ? {} : { level }),
  }
}

/** Pose une forme **au-dessus** des précédentes : l'ordre du tableau est celui du dessin. */
export function addNode(schematic: NetworkSchematic, node: SchematicNode): NetworkSchematic {
  return { ...schematic, nodes: [...schematic.nodes, node] }
}

/** Retire une forme. Une forme absente n'est pas une erreur : le dessin est déjà comme voulu. */
export function removeNode(schematic: NetworkSchematic, id: string): NetworkSchematic {
  return { ...schematic, nodes: schematic.nodes.filter((node) => node.id !== id) }
}

/**
 * Fait glisser une forme : **tous** ses points bougent du même écart.
 *
 * Déplacer le seul point d'ancrage déformerait une conduite au lieu de la déplacer — et la
 * déformation ne se voit qu'à trois segments, donc pas sur l'exemple qu'on essaie en premier.
 */
export function moveNode(
  schematic: NetworkSchematic,
  id: string,
  delta: SchematicPoint,
): NetworkSchematic {
  const shifted = (point: SchematicPoint): SchematicPoint => ({
    ...point,
    x: point.x + delta.x,
    y: point.y + delta.y,
  })

  // ⚠️ **Une conduite accrochée suit l'ouvrage qu'on déplace**, par son extrémité seule : c'est
  // ce que l'accroche existe pour faire. Sans elle, déplacer un regard laissait la conduite
  // pointer dans le vide, et l'agent redessinait le branchement.
  return {
    ...schematic,
    nodes: schematic.nodes.map((node) => {
      if (node.id === id) return { ...node, points: node.points.map(shifted) }
      if (node.from !== id && node.to !== id) return node

      const points = [...node.points]
      const last = points.length - 1
      if (node.from === id && points[0] !== undefined) points[0] = shifted(points[0])
      if (node.to === id && points[last] !== undefined && last > 0)
        points[last] = shifted(points[last])
      return { ...node, points }
    }),
  }
}

/** Tire un angle d'une forme — le sommet `index` va au point atteint. */
export function moveVertex(
  schematic: NetworkSchematic,
  id: string,
  index: number,
  point: SchematicPoint,
): NetworkSchematic {
  return mapNode(schematic, id, (node) => ({
    ...node,
    points: node.points.map((current, i) => (i === index ? point : current)),
  }))
}

/**
 * Ajoute un angle **au milieu d'un mur** : le nouveau point prend la place `index` du tour, et
 * les suivants se décalent. C'est le double appui de GHP DIGIT, en un geste visible.
 */
export function insertVertex(
  schematic: NetworkSchematic,
  id: string,
  index: number,
  point: SchematicPoint,
): NetworkSchematic {
  return mapNode(schematic, id, (node) => ({
    ...node,
    points: [...node.points.slice(0, index), point, ...node.points.slice(index)],
  }))
}

/**
 * Pose le point suivant d'une forme tracée **d'appui en appui** — maison, préau, conduite coudée.
 *
 * ⚠️ Un appui sur le dernier point posé ne compte pas : le doigt qui rebondit toucherait deux fois
 * le même endroit, et la maison gagnerait un mur de longueur nulle, invisible et intouchable.
 */
export function appendPoint(
  schematic: NetworkSchematic,
  id: string,
  point: SchematicPoint,
): NetworkSchematic {
  return mapNode(schematic, id, (node) => {
    const last = node.points.at(-1)
    if (last !== undefined && last.x === point.x && last.y === point.y) return node
    return { ...node, points: [...node.points, point] }
  })
}

/**
 * Retire un angle. ⚠️ Refusé en dessous de trois angles pour une maison et de deux pour un
 * trait : la forme ne serait plus ce que l'agent a posé, et rien ne permettrait de la reprendre.
 */
export function removeVertex(
  schematic: NetworkSchematic,
  id: string,
  index: number,
): NetworkSchematic {
  return mapNode(schematic, id, (node) => {
    const minimum = node.kind === 'polygon' ? 3 : 2
    if (node.points.length <= minimum) return node
    return { ...node, points: node.points.filter((_, i) => i !== index) }
  })
}

/** Les demi-côtés des symboles posés d'un appui, en unités de document. */
const EXTENT: Readonly<Record<string, number>> = {
  manhole: 17,
  trap: 17,
  inspectionChamber: 17,
  gratedInlet: 17,
  septicTank: 34,
  manholeCover: 26,
  downpipe: 14,
  plug: 14,
  floorDrain: 14,
  liftingStation: 14,
  trapHorizontal: 23,
  trapVertical: 23,
}

/**
 * Le demi-côté d'un symbole posé d'un appui — ce que le doigt touche et là où une conduite
 * s'arrête. `0` pour ce qui n'est pas un ouvrage.
 *
 * ⚠️ **`scale` n'est plus lu** (#581, « Taille des Ouvrages : fixe ») : un symbole qu'un plan
 * existant avait agrandi se lit et se dessine à sa taille de base. Le champ reste dans le
 * dessin, qui ne se migre pas (M21).
 */
export function symbolExtent(node: SchematicNode): number {
  return EXTENT[node.kind] ?? 0
}

/** Les étiquettes posées d'un appui : du texte, sans symbole. */
export function isTag(node: SchematicNode): boolean {
  return node.kind === 'roomLabel' || node.kind === 'outletLabel'
}

/** Le texte d'une étiquette ; celle d'une pièce porte son étage : « wc (−1) ». */
export function tagText(node: SchematicNode): string | null | undefined {
  if (node.kind !== 'roomLabel' || typeof node.level !== 'number') return node.text
  return `${node.text ?? ''} (${String(node.level).replace('-', '−')})`
}

/**
 * La boîte d'une étiquette, écrite à partir de son point en police mono de 26 unités : seize
 * unités par caractère, la ligne de base au point. La zone de toucher et le cadre de l'image
 * d'archive la lisent toutes deux.
 */
export function tagBox(node: SchematicNode): SchematicViewport | null {
  const [point] = node.points
  if (point === undefined || !isTag(node)) return null
  const length = (tagText(node) ?? '').length
  return { x: point.x - 6, y: point.y - 28, width: length * 16 + 12, height: 36 }
}

/** L'ouvrage dont le symbole est sous le point, le plus récent en premier — ou `null`. */
export function structureAt(
  schematic: NetworkSchematic,
  point: SchematicPoint,
): SchematicNode | null {
  for (let i = schematic.nodes.length - 1; i >= 0; i--) {
    const node = schematic.nodes[i]
    const center = node?.points[0]
    if (node === undefined || center === undefined || node.points.length !== 1) continue
    const extent = symbolExtent(node)
    if (
      extent > 0 &&
      Math.abs(point.x - center.x) <= extent &&
      Math.abs(point.y - center.y) <= extent
    ) {
      return node
    }
  }
  return null
}

/**
 * Le point du bord d'un ouvrage **du côté d'où vient la conduite** : c'est là qu'elle s'arrête,
 * pour que sa pointe se voie au lieu de se perdre au centre du symbole.
 */
export function edgeToward(node: SchematicNode, toward: SchematicPoint): SchematicPoint {
  const center = node.points[0] ?? { x: 0, y: 0 }
  const dx = toward.x - center.x
  const dy = toward.y - center.y
  const length = Math.hypot(dx, dy)
  if (length === 0) return center
  const extent = symbolExtent(node)
  return {
    x: Math.round((center.x + (dx / length) * extent) * 10) / 10,
    y: Math.round((center.y + (dy / length) * extent) * 10) / 10,
  }
}

/** Accroche une conduite à l'ouvrage de départ, d'arrivée, ou aux deux. */
export function setNodeEnds(
  schematic: NetworkSchematic,
  id: string,
  ends: { from?: string | null; to?: string | null },
): NetworkSchematic {
  return mapNode(schematic, id, (node) => ({ ...node, ...ends }))
}

/**
 * Change le réseau d'un ouvrage sans le reposer. Sans effet sur un élément qui ne se type pas :
 * une conduite privée porte son réseau dans son `kind`, et la repeindre ferait une « conduite
 * EU » bleue, la couleur de l'EP. Sans effet non plus sur un caniveau qu'on dirait unitaire.
 */
export function setNodeNetwork(
  schematic: NetworkSchematic,
  id: string,
  network: SchematicNetwork | 'ink',
): NetworkSchematic {
  return mapNode(schematic, id, (node) => {
    const accepted = acceptedNetwork(node.kind, network)
    return accepted === undefined ? node : { ...node, stroke: accepted }
  })
}

/** Change l'étage d'une étiquette de pièce. */
export function setNodeLevel(
  schematic: NetworkSchematic,
  id: string,
  level: number,
): NetworkSchematic {
  return mapNode(schematic, id, (node) => ({ ...node, level }))
}

/**
 * La distance minimale entre deux points d'un tracé, en unités de document — environ un pixel
 * et demi à l'écran au repos.
 *
 * ⚠️ Sans ce filtre, **chaque** événement de déplacement pose un point : la tablette en émet un
 * par rafraîchissement, y compris quand la main tremble sur place, et le doigt bouge bien moins
 * vite que l'écran ne rafraîchit. Sur un tracé réel, il en retire la grande majorité sans qu'on
 * voie la différence — et ce qu'on retire est du poids dans le corps de la proposition, que le
 * dépôt plafonne à 1 Mo.
 */
export const MIN_FREEHAND_TRAVEL = 3

/**
 * Allonge un tracé du point atteint — le geste du crayon, qui suit le doigt.
 *
 * ⚠️ Réservé au tracé libre. Une conduite ou une flèche se tire par son extrémité
 * ({@link dragNodeEnd}) : y accumuler les points ferait d'un trait droit une polyligne de cent
 * segments, qui pèse dans le relevé transmis et se déforme au moindre déplacement.
 *
 * ⚠️ **Deux points ne sont pas posés** : celui que le doigt n'a pas vraiment quitté
 * ({@link MIN_FREEHAND_TRAVEL}), et tous ceux qui suivent le plafond de la forme
 * (`MAX_SCHEMATIC_NODE_POINTS`). Arrêter d'écrire, jamais effacer : le tracé déjà posé reste
 * entier, et l'agent en est averti à la transmission (`hasTruncatedSchematicStroke`, M21).
 */
export function extendNode(
  schematic: NetworkSchematic,
  id: string,
  point: SchematicPoint,
): NetworkSchematic {
  return mapNode(schematic, id, (node) => {
    const last = node.points.at(-1)

    if (node.points.length >= MAX_SCHEMATIC_NODE_POINTS) return node
    if (last !== undefined && travelOf(last, point) < MIN_FREEHAND_TRAVEL) return node

    return { ...node, points: [...node.points, point] }
  })
}

/**
 * Allège un tracé à main levée au relâchement : un point qui s'écarte de moins de `tolerance`
 * de la droite joignant ses voisins retenus disparaît (Douglas-Peucker). Le premier et le dernier
 * restent.
 *
 * ⚠️ Le filtre du geste ({@link MIN_FREEHAND_TRAVEL}) ne suffit pas : il garde un point par
 * pixel et demi, donc des centaines sur un trait, dont chacun pèse dans le relevé et se repeint
 * à chaque déplacement de la forme. Une ligne droite tracée à la main retombe ici à deux points.
 */
export function simplifyPath(points: SchematicPoint[], tolerance: number): SchematicPoint[] {
  if (points.length <= 2) return points

  const kept = new Set([0, points.length - 1])
  const spans: [number, number][] = [[0, points.length - 1]]
  for (let span = spans.pop(); span !== undefined; span = spans.pop()) {
    const [from, to] = span
    const a = points[from]
    const b = points[to]
    if (a === undefined || b === undefined) continue

    let farthest = -1
    let offset = tolerance
    for (let index = from + 1; index < to; index++) {
      const point = points[index]
      const distance = point === undefined ? 0 : offsetFrom(point, a, b)
      if (distance > offset) {
        offset = distance
        farthest = index
      }
    }
    if (farthest !== -1) {
      kept.add(farthest)
      spans.push([from, farthest], [farthest, to])
    }
  }

  return points.filter((_, index) => kept.has(index))
}

/** La distance d'un point à la droite `a`–`b`, ou à `a` quand les deux se confondent. */
function offsetFrom(point: SchematicPoint, a: SchematicPoint, b: SchematicPoint): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const length = Math.hypot(dx, dy)
  if (length === 0) return travelOf(a, point)
  return Math.abs(dx * (a.y - point.y) - dy * (a.x - point.x)) / length
}

function travelOf(from: SchematicPoint, to: SchematicPoint): number {
  return Math.hypot(to.x - from.x, to.y - from.y)
}

/** Tire l'extrémité d'une forme à deux points — conduite droite, ligne, flèche, boîte. */
export function dragNodeEnd(
  schematic: NetworkSchematic,
  id: string,
  point: SchematicPoint,
): NetworkSchematic {
  return mapNode(schematic, id, (node) => ({
    ...node,
    points: [...node.points.slice(0, -1), point],
  }))
}

/** Écrit l'étiquette d'une forme — les « EU », « EP » et « épandage » de la maquette. */
export function setNodeText(
  schematic: NetworkSchematic,
  id: string,
  text: string,
): NetworkSchematic {
  return mapNode(schematic, id, (node) => ({ ...node, text }))
}

/**
 * L'écart à l'horizontale ou à la verticale que « Aligner » redresse, en degrés — la valeur de
 * GHP DIGIT (`maxAngle`). Au-delà, le mur est un pan coupé voulu : il reste tel quel.
 */
export const MAX_STRAIGHTEN_ANGLE = 10

/**
 * « Aligner » : redresse les segments presque horizontaux ou presque verticaux d'une forme —
 * le bouton équerre de GHP DIGIT, **appliqué après coup** à la forme choisie, jamais pendant le
 * tracé.
 *
 * Chaque point se cale sur le précédent : le premier ne bouge pas, et une suite de murs
 * redressés reste d'équerre. Sur une maison (`polygon`), le dernier point se cale aussi sur le
 * premier, pour fermer le dernier mur ; un tracé ouvert n'en a pas — GHP DIGIT le fermait
 * quand même, ce qui n'est pas repris.
 */
export function straightenNode(
  schematic: NetworkSchematic,
  id: string,
  maxAngle = MAX_STRAIGHTEN_ANGLE,
): NetworkSchematic {
  return mapNode(schematic, id, (node) => ({
    ...node,
    points: straightened(node.points, node.kind === 'polygon', maxAngle),
  }))
}

function straightened(
  points: SchematicPoint[],
  closed: boolean,
  maxAngle: number,
): SchematicPoint[] {
  const out: SchematicPoint[] = []
  for (const point of points) {
    const previous = out.at(-1)
    out.push(previous === undefined ? point : alignedTo(point, previous, maxAngle))
  }

  const [first] = out
  const last = out.at(-1)
  if (closed && out.length > 2 && first !== undefined && last !== undefined) {
    out[out.length - 1] = alignedTo(last, first, maxAngle)
  }
  return out
}

/** `point`, calé sur `anchor` si le segment qui les joint est presque horizontal ou vertical. */
function alignedTo(
  point: SchematicPoint,
  anchor: SchematicPoint,
  maxAngle: number,
): SchematicPoint {
  const degrees = Math.abs((Math.atan2(point.y - anchor.y, point.x - anchor.x) * 180) / Math.PI)

  if (degrees <= maxAngle || degrees >= 180 - maxAngle) return { ...point, y: anchor.y }
  if (Math.abs(degrees - 90) <= maxAngle) return { ...point, x: anchor.x }
  return point
}

/**
 * Cale un point sur la grille de pas `step`, en unités de document.
 *
 * ⚠️ Vient de l'essai retenu le 29/09/2026, pas de GHP DIGIT, dont le quadrillage n'était
 * qu'un repère visuel, sans aimantation.
 */
export function snapToGrid(point: SchematicPoint, step: number): SchematicPoint {
  return { ...point, x: Math.round(point.x / step) * step, y: Math.round(point.y / step) * step }
}

function mapNode(
  schematic: NetworkSchematic,
  id: string,
  change: (node: SchematicNode) => SchematicNode,
): NetworkSchematic {
  return {
    ...schematic,
    nodes: schematic.nodes.map((node) => (node.id === id ? change(node) : node)),
  }
}
